import { useState } from "react";
import { Lock } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { UploadZone } from "../../lib/resumeUpload.js";
import { Badge } from "../ui/badge.js";
import { useConfirm } from "../ui/confirm-dialog.js";
import { UploadedFile } from "../ui/uploaded-file.js";
import { DocumentPreviewDialog } from "./DocumentPreviewDialog.js";

/** The document already on file for a slot (from the employee's document list). */
export interface SlotDoc { id: string; name: string; status: string; mimeType?: string | null | undefined }

/**
 * One document field on the onboarding wizard: the upload zone while nothing is on file,
 * and once a file is there, its name with edit (replace) and delete icons inside the field.
 * Replacing goes through `onFile`, the same handler as a first upload. Deleting asks first,
 * then calls `onDeleted` so the page can reload its document list.
 */
export function DocumentSlot({ doc, label, accept, onFile, onDeleted, loading, disabled, disabledReason, className }: {
  doc: SlotDoc | undefined;
  /** The upload zone's text, shown while nothing is on file. */
  label: string;
  accept?: string;
  onFile: (file: File) => void;
  onDeleted: () => void;
  /** A file is uploading or being checked. */
  loading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  /** Sizes the uploaded-file row (the upload zone keeps its own width). */
  className?: string;
}) {
  const confirm = useConfirm();
  const [deleting, setDeleting] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [err, setErr] = useState("");

  // An upload that started but never finished is not a file on file.
  if (!doc || doc.status === "PENDING_UPLOAD") {
    return <UploadZone onFile={onFile} label={label} loading={loading} disabled={disabled} disabledReason={disabledReason}
      {...(accept ? { accept } : {})} />;
  }

  const verified = doc.status === "VERIFIED";
  const rejected = doc.status === "REJECTED";

  async function remove() {
    if (!doc) return;
    const ok = await confirm({
      title: "Delete this file?",
      description: `${doc.name} will be removed. You can upload another one.`,
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    setDeleting(true); setErr("");
    try {
      await api.delete(`/documents/${doc.id}`);
      onDeleted();
    } catch (e) {
      logError("DocumentSlot.delete", e, { docId: doc.id });
      setErr(errMsg(e) ?? "The file could not be deleted. Please try again.");
    } finally { setDeleting(false); }
  }

  return (
    <div className="min-w-0">
      <UploadedFile
        name={doc.name}
        tone={rejected ? "error" : "success"}
        busy={!!loading || deleting}
        busyLabel={deleting ? "Deleting…" : "Uploading…"}
        accept={accept}
        className={className}
        badge={verified
          ? <Badge tone="success" className="gap-1"><Lock size={11} /> Locked</Badge>
          : rejected ? <Badge tone="error">Rejected</Badge> : undefined}
        onView={() => setViewing(true)}
        onReplace={verified ? undefined : onFile}
        onDelete={verified ? undefined : () => void remove()}
      />
      {err && <p className="mt-1 text-xs text-[var(--color-error-fg)]">{err}</p>}
      <DocumentPreviewDialog doc={doc} open={viewing} onOpenChange={setViewing} />
    </div>
  );
}
